import { occurrenceKey, type Checkin, type CheckinStatus } from '@planer/shared';

export type StatusLookup = (key: string) => CheckinStatus | null | undefined;

/**
 * Server state with not-yet-synced local changes on top, so a check-in made offline is shown
 * immediately and survives refetches until the outbox has delivered it.
 */
export function buildStatusLookup(
  server: readonly Checkin[] | undefined,
  pending: ReadonlyMap<string, Checkin>,
): StatusLookup {
  const map = new Map<string, CheckinStatus | null>();
  for (const c of server ?? []) map.set(occurrenceKey(c.itemId, c.date), c.status);
  for (const [key, c] of pending) map.set(key, c.status);
  return (key) => map.get(key);
}

/** Writes check-ins into every cached `['checkins', from, to]` range they belong to. */
export function mergeCheckins(
  cached: { checkins: Checkin[] } | undefined,
  range: { from: string; to: string },
  applied: readonly Checkin[],
): { checkins: Checkin[] } | undefined {
  if (!cached) return cached;
  const relevant = applied.filter((c) => c.date >= range.from && c.date <= range.to);
  if (relevant.length === 0) return cached;
  const byKey = new Map(cached.checkins.map((c) => [occurrenceKey(c.itemId, c.date), c]));
  for (const c of relevant) byKey.set(occurrenceKey(c.itemId, c.date), c);
  return { checkins: [...byKey.values()] };
}
